import { Component, HostListener, OnInit, OnDestroy } from "@angular/core";
import { Subject, combineLatest, of, timer } from "rxjs";
import { take, takeUntil, catchError, switchMap, skip } from "rxjs/operators";
import { ManagerService } from "../../core/services/manager.service";
import { DataManagerService, TrbTheme, TrbSummaryResponse, PayoutSchedule } from "../../core/services/data-manager.service";
import { NotificationsService } from "../../core/notifications/notifications.service";
import { ConfigService } from "../../core/services/config.service";

@Component({
  selector: "app-true-rakeback",
  templateUrl: "./true-rakeback.component.html",
  styleUrls: ["./true-rakeback.component.scss"],
})
export class TrueRakebackComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  trbThemes: TrbTheme[] = [];
  selectedTheme: TrbTheme | null = null;
  summary: TrbSummaryResponse | null = null;
  loading = true;
  loadingSummary = false;
  currentCarouselIndex = 0;
  currentPayoutSchedules: PayoutSchedule[] = [];
  user: any;
  newDesign: boolean = false;
  noData = false;
  themeMenuOpen = false;

  constructor(
    private managerService: ManagerService,
    private dataManager: DataManagerService,
    private notificationsService: NotificationsService,
    private configService: ConfigService
  ) {
    this.newDesign = this.configService.config?.newDesign || false;
    this.managerService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.user = user;
    });
  }

  ngOnInit(): void {
    this.managerService.trbThemes$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((themes) => {
        this.applyThemes(themes);
      });

    this.managerService.trbRewardCollected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.selectedTheme) {
          this.loadSummary(this.selectedTheme.id);
        }
      });

    this.managerService.fetchTrbThemes(true);
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    if (!this.themeMenuOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    if (!target.closest(".theme-selector")) {
      this.themeMenuOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyThemes(themes: TrbTheme[]) {
    this.loading = false;
    if (!themes || themes.length === 0) {
      this.trbThemes = [];
      this.selectedTheme = null;
      this.noData = true;
      return;
    }

    this.noData = false;
    this.trbThemes = themes;
    const previousId = this.selectedTheme?.id;
    const nextTheme =
      themes.find((theme) => theme.id === previousId) || themes[0];
    const themeChanged = !this.selectedTheme || this.selectedTheme.id !== nextTheme.id;
    this.selectedTheme = nextTheme;
    this.currentPayoutSchedules = this.selectedTheme.payoutSchedules || [];
    if (themeChanged || !this.summary) {
      this.currentCarouselIndex = 0;
      this.loadSummary(this.selectedTheme.id);
    }
  }

  loadSummary(themeId: number) {
    this.loadingSummary = true;
    combineLatest([this.managerService.user$, this.dataManager.getGameToken()])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([user, response]: [any, any]) => {
          const token = response.token;
          if (!token) {
            this.loadingSummary = false;
            return of(null);
          }

          return this.dataManager.getTrbSummary(themeId, token).pipe(
            catchError((error) => {
              console.error("Error loading TRB summary:", error);
              this.loadingSummary = false;
              return of(null);
            })
          );
        })
      )
      .subscribe((response: TrbSummaryResponse | null) => {
        this.loadingSummary = false;
        if (response && response.Success) {
          this.summary = response;
          // Set carousel index to the level that matches player's VPIP
          this.setCarouselIndexForPlayerVPIP();
        }
      });
  }

  onThemeChange(theme: TrbTheme) {
    this.selectedTheme = theme;
    this.currentPayoutSchedules = theme.payoutSchedules || [];
    this.currentCarouselIndex = 0;
    this.loadingSummary = true; // Show loading when changing theme
    this.loadSummary(theme.id);
    // Note: setCarouselIndexForPlayerVPIP will be called after summary loads
  }

  toggleThemeMenu(event: Event) {
    event.stopPropagation();
    this.themeMenuOpen = !this.themeMenuOpen;
  }

  selectTheme(theme: TrbTheme, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.themeMenuOpen = false;
    if (!theme || theme.id === this.selectedTheme?.id) {
      return;
    }
    this.onThemeChange(theme);
  }

  collectRakeback(historyItem: any) {
    if (historyItem.isPaid) {
      return;
    }
    this.managerService.unlockTrueRakeback(historyItem.giftId);
    timer(1200)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.managerService.fetchTrbThemes(true);
        if (this.selectedTheme) {
          this.loadSummary(this.selectedTheme.id);
        }
      });
  }

  getCurrentPayoutSchedule() {
    if (!this.selectedTheme || !this.summary) {
      return null;
    }

    const vpip = this.summary.vpipPercentage;
    return this.selectedTheme.payoutSchedules.find(
      (schedule) => vpip >= schedule.min && vpip <= schedule.max
    ) || this.selectedTheme.payoutSchedules[0];
  }

  getThemeDisplayName(theme: TrbTheme): string {
    // Format theme name for display (e.g., "micron" -> "NLH - Micro (USD)")
    const nameMap: { [key: string]: string } = {
      micron: "NLH - Micro (USD)",
      low: "NLH - Low (USD)",
      mid: "NLH - Mid (USD)",
    };
    return nameMap[theme.name] || theme.name.toUpperCase();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear().toString().slice(-2)}`;
  }

  formatDateRange(from: string, to: string): string {
    return `${this.formatDate(from)} - ${this.formatDate(to)}`;
  }

  getRakebackRange(schedule: PayoutSchedule): string {
    const minValue = schedule.min * schedule.multiplier;
    const maxValue = schedule.max * schedule.multiplier;
    const cap = this.selectedTheme?.maxRakebackPercentage || 0;
    const format = (value: number) => this.formatPercentage(value);

    if (cap <= 0) {
      return `${format(minValue)}%-${format(maxValue)}%`;
    }

    if (maxValue <= cap) {
      return `${format(minValue)}%-${format(maxValue)}%`;
    }

    if (minValue >= cap) {
      return `${format(cap)}%`;
    }

    return `${format(minValue)}%-${format(cap)}%`;
  }

  getRakebackCapLabel(schedule: PayoutSchedule): string | null {
    const cap = this.selectedTheme?.maxRakebackPercentage || 0;
    if (cap <= 0) {
      return null;
    }

    const maxValue = schedule.max * schedule.multiplier;
    if (maxValue <= cap) {
      return null;
    }

    return `(Capped at ${this.formatPercentage(cap)}%)`;
  }

  private formatPercentage(value: number): string {
    const rounded = Math.round(value * 10) / 10;
    const fixed = rounded.toFixed(1);
    return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
  }

  previousCarousel() {
    if (this.currentCarouselIndex > 0) {
      this.currentCarouselIndex--;
    }
  }

  nextCarousel() {
    if (this.currentCarouselIndex < this.currentPayoutSchedules.length - 1) {
      this.currentCarouselIndex++;
    }
  }

  goToCarouselIndex(index: number) {
    if (index >= 0 && index < this.currentPayoutSchedules.length) {
      this.currentCarouselIndex = index;
    }
  }

  // Expose Math to template
  Math = Math;

  setCarouselIndexForPlayerVPIP() {
    if (!this.summary || !this.currentPayoutSchedules || this.currentPayoutSchedules.length === 0) {
      return;
    }

    const vpip = this.summary.vpipPercentage;
    const matchingIndex = this.currentPayoutSchedules.findIndex(
      (schedule) => vpip >= schedule.min && vpip <= schedule.max
    );

    if (matchingIndex !== -1) {
      this.currentCarouselIndex = matchingIndex;
    } else {
      // If no exact match, find the closest level
      this.currentCarouselIndex = 0;
    }
  }
}

